<template>
  <div>
    <CommonBreadCrumbs :data="breadcrumbs"></CommonBreadCrumbs>
    <div>
      <NuxtLayout name="center">
        <h1>{{ testData.title }}</h1>
        <NuxtLink
          class="text-lg-24 underline hover:no-underline font-semibold text-primary cursor-pointer"
          @click="linkTitle('.related-service')"
          v-if="relatedServicesOptions.length > 1"
        >
          <div class="flex justify-center">
            <IconsArrowDownward class="mx-1 self-center md:hidden" />
            <span>
              Available From
              <span class="whitespace-nowrap">{{ lowestPrice }}</span>
            </span>
            <IconsArrowRight class="mx-1 self-center md:block hidden" />
          </div>
        </NuxtLink>
      </NuxtLayout>
      <NuxtLayout name="center">
        <div class="md:mx-0 mx-4">
          <div class="md:grid w-full md:grid-cols-2 items-start gap-y-8 md:gap-x-20 lg:gap-x-32 xl:gap-x-40 mt-12">
            <div class="space-y-10">
              <div class="space-y-10">
                <!-- Primary Image -->
                <div class="relative rounded-lg overflow-hidden">
                  <!-- Top header element -->
                  <div :class="[titleBarBackground, 'px-4 py-1 flex justify-between items-center relative z-10']">
                    <span class="text-white text-[14px] font-semibold">{{ pickedServiceType.title }}</span>
                    <div
                      :class="[
                        titleBarBackground,
                        'flex items-center justify-center absolute rounded-full -bottom-[13px] right-4 w-10 h-10',
                      ]"
                    >
                      <NuxtImg loading="lazy" :src="serviceIcon" alt="" class="w-5 h-5" width="20" height="20" />
                    </div>
                  </div>
                  <NuxtImg
                    loading="lazy"
                    v-if="primaryImage"
                    :src="primaryImage?.permalink"
                    :alt="primaryImage?.alt"
                    class="h-full w-full object-center object-cover aspect-square"
                  />
                  <NuxtImg
                    loading="lazy"
                    v-else
                    src="/img/91502410.webp"
                    class="object-center object-cover bg-grey-light aspect-square"
                    alt=""
                  />

                  <!-- Bottom Tags -->
                  <div class="absolute bottom-0 left-0 w-full z-10 flex justify-between pb-6">
                    <!-- Left Tag -->
                    <span
                      v-if="testData.left_tag"
                      class="bg-secondary text-white text-xs font-semibold px-3 py-1 rounded-tr-[10px] rounded-br-[10px]"
                    >
                      {{ testData.left_tag }}
                    </span>

                    <!-- Right Tag -->
                    <span
                      v-if="testData.right_tag"
                      class="bg-golden-banner text-space-cadet text-xs font-semibold px-3 py-1 rounded-tl-[10px] rounded-bl-[10px]"
                    >
                      {{ testData.right_tag }}
                    </span>
                  </div>
                </div>

                <!-- Images Carousel -->
                <div
                  class="w-full overflow-hidden"
                  v-if="testData.additional_media && testData.additional_media.length > 0"
                >
                  <Carousel
                    :key="shouldShowNavigation ? 1 : 0"
                    :itemsToShow="1"
                    :itemsToScroll="1"
                    :wrap-around="false"
                    :mouse-drag="true"
                    :snap-align="'start'"
                    class="w-full"
                    :breakpoints="breakpoints"
                  >
                    <Slide v-for="(data, index) in testData.additional_media" :key="index">
                      <div
                        v-if="data.media_type === 'image' && data.image?.permalink"
                        class="px-2 w-full max-w-[150px] mx-auto cursor-pointer"
                      >
                        <NuxtImg
                          loading="lazy"
                          :src="data.image.permalink"
                          :alt="data.image?.alt"
                          @click="setPrimaryImage(data.image)"
                          class="w-full h-full object-center object-cover aspect-square max-w-[150px]"
                        />
                      </div>
                      <div
                        v-else-if="data.media_type === 'video' && data.thumbnail?.permalink"
                        class="relative px-2 w-full max-w-[150px] mx-auto cursor-pointer"
                      >
                        <NuxtImg
                          loading="lazy"
                          :src="data.thumbnail.permalink"
                          :alt="data.thumbnail?.alt"
                          @click="openVideoModal(data.video)"
                          class="w-full h-full object-center object-cover aspect-square max-w-[150px]"
                        />

                        <!-- Centered Video Icon (Non-blocking) -->
                        <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div class="bg-black bg-opacity-60 rounded-full p-2">
                            <IconsVideoIcon class="w-10 h-10" />
                          </div>
                        </div>
                      </div>
                    </Slide>

                    <template #addons>
                      <CarouselNavigation v-if="shouldShowNavigation">
                        <template #prev>
                          <span class="bg-secondary rounded-full p-2 flex items-center justify-center">
                            <IconsNavigationArrowLeft color="white" class="w-3 h-3" />
                          </span>
                        </template>
                        <template #next>
                          <span class="bg-secondary rounded-full p-2 flex items-center justify-center">
                            <IconsNavigationArrowRight color="white" class="w-3 h-3" />
                          </span>
                        </template>
                      </CarouselNavigation>
                    </template>
                  </Carousel>
                </div>
              </div>
              <div class="related-service hidden md:block" v-if="relatedServicesOptions.length > 1">
                <h2 class="flex text-2xl font-bold mb-4">Available As</h2>

                <div
                  class="flex flex-col border-b border-light-gray"
                  v-for="(relatedData, index) in relatedServicesOptions"
                  :key="index"
                >
                  <div class="text-lg py-1">
                    <ProductRadioButton
                      :id="relatedData.id"
                      :title="relatedData?.service_type?.title"
                      :slug="relatedData.slug"
                      name="serviceType"
                      :extraText="$formatCurrency(relatedData.price)"
                      v-model="picked"
                      @change="updateRadioAndMove()"
                    ></ProductRadioButton>
                  </div>
                </div>
                <NuxtLink class="underline text-secondary cursor-pointer">
                  <div class="flex justify-center mt-4">
                    <NuxtImg
                      loading="lazy"
                      src="/icons/arrow-down.svg"
                      class="mx-1"
                      alt="down arrow icon"
                      width="16"
                      height="16"
                    />
                    <span @click="linkTitle('.how-it-works-wrapper')">Compare Our Services</span>
                  </div>
                </NuxtLink>
              </div>
              <ClinicHelpBlock v-else class="mt-0 space-y-2 bg-primary-light hidden md:block"></ClinicHelpBlock>
            </div>
            <div class="mt-8 md:mt-0">
              <div class="flex items-end border-b border-primary">
                <h2 class="text-2xl font-bold mb-[3px]">
                  {{ testData.service_type.title }}
                </h2>
                <div class="ml-auto rtl:mr-auto rtl:ml-0 font-bold text-[29px] text-primary whitespace-nowrap">
                  {{ $formatCurrency(testData.price) }}
                </div>
              </div>
              <p class="my-4">{{ testData.summary }}</p>

              <div v-if="testData.description">
                <NuxtLink @click="linkTitle('.product-info')" class="cursor-pointer text-secondary underline">
                  <div class="flex mt-2">
                    <span>{{ $t("testDetail.viewFullInfo") }}</span>
                    <NuxtImg
                      loading="lazy"
                      src="/icons/arrow-right-secondary.svg"
                      class="mx-1"
                      alt="secondary right arrow"
                      width="9"
                      height="28"
                    />
                  </div>
                </NuxtLink>
              </div>
              <div class="flex flex-col text-space-cadet my-10">
                <div
                  class="flex font-semibold py-2 border-b border-light-gray md:items-center items-center"
                  v-for="(testAddditionalDetail, index) in testAdditionalDetails"
                  :key="index"
                >
                  <div class="whitespace-nowrap">{{ testAddditionalDetail.title }}:</div>
                  <span class="font-bold mx-2">
                    {{ testAddditionalDetail.data }}
                  </span>
                  <NuxtLink class="cursor-pointer min-w-[14px]" @click="onIconclick(testAddditionalDetail)">
                    <NuxtImg loading="lazy" alt="" src="/icons/c-info.svg" width="16" />
                  </NuxtLink>
                </div>
              </div>

              <CommonProductModal
                @on-modal-close="closeModal"
                :title="modalDetails.title"
                :description="modalDetails.description"
                :descriptionList="modalDetails.descriptionList"
                :dataProp="modalDetails.dataProp"
                :showModal="showModal"
              />

              <div class="flex border-b border-primary">
                <h2 class="text-2xl font-bold">
                  {{ $t("testDetail.testsFor") }}
                </h2>
              </div>
              <div
                class="mt-4 mb-8 md:mb-12 grid grid-cols-2 grid-flow-col infection-rows"
                :style="{
                  '--rows': infectionsRow,
                }"
              >
                <ClinicInfectionData
                  :data="infectionData"
                  v-for="(infectionData, index) in testData.infections"
                  :key="index"
                ></ClinicInfectionData>
              </div>

              <div v-for="(test, index) in testData.children" class="mt-8">
                <ProductDependentChild :childTest="test" class="mb-10"></ProductDependentChild>
              </div>

              <ClinicHelpBlock
                class="md:mb-12 space-y-2 bg-primary-light"
                :class="{
                  'block md:hidden': relatedServicesOptions.length <= 1,
                  '': relatedServicesOptions.length > 1,
                }"
              ></ClinicHelpBlock>

              <div class="mt-8 md:mt-0" v-if="pickedServiceType?.title == 'Home Test Kit'">
                <div>
                  <p
                    class="text-base leading-4 font-semibold mb-[10px]"
                    :class="stock == 'in' ? 'text-antigua' : 'text-brand-orange'"
                  >
                    {{ stockStatus }}
                  </p>
                </div>
                <div class="flex gap-4" v-if="stock && stock !== 'out'">
                  <CartAddToCartWidget :product="kitProduct" />
                </div>

                <div v-else>
                  <BaseButton
                    :text="$t('testDetail.notifyMe')"
                    class="w-full h-[60px] text-[22px]"
                    @click="openOutStockModal"
                  ></BaseButton>
                </div>

                <a
                  v-if="showSubscribeBlock"
                  :href="subscriptionLink"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="group mt-8 flex flex-col gap-4 rounded-md bg-primary-light px-[18px] py-4 text-inherit no-underline lg:flex-row lg:items-center lg:gap-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
                >
                  <div class="flex min-w-0 flex-1 gap-4">
                    <div
                      class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-secondary/20 bg-white"
                      aria-hidden="true"
                    >
                      <NuxtImg
                        loading="lazy"
                        src="/icons/repeat.svg"
                        alt=""
                        class="h-[22px] w-[22px]"
                        width="22"
                        height="22"
                      />
                    </div>
                    <div class="min-w-0 flex-1">
                      <div class="mb-1 flex flex-wrap items-center gap-2.5">
                        <span class="text-base font-semibold text-dark-blue">
                          {{ $t("testDetail.subscribeTitle") }}
                        </span>
                        <span
                          v-if="String(globalStore.siteSettings?.subscription_discount_label ?? '').trim()"
                          class="rounded bg-golden-banner px-2 py-0.5 text-[11px] font-semibold tracking-wide text-space-cadet"
                        >
                          {{ globalStore.siteSettings?.subscription_discount_label }}
                        </span>
                      </div>
                      <p>
                        {{ $t("testDetail.subscribeTagline") }}
                      </p>
                    </div>
                  </div>
                  <span
                    class="flex w-full shrink-0 items-center justify-center whitespace-nowrap rounded border-[1.5px] border-secondary px-[18px] py-2.5 text-sm font-semibold text-secondary lg:inline-flex lg:w-auto"
                  >
                    {{ $t("testDetail.subscribeCta") }}
                    <span class="ml-0.5" aria-hidden="true">→</span>
                  </span>
                </a>
              </div>

              <BaseButton
                :text="$t('testDetail.bookAppointment')"
                v-else
                class="w-full h-[60px] text-[22px] md:mt-0 mt-8"
                @click="openDrawerMap(testData)"
              ></BaseButton>

              <ClinicSliderList
                :serviceType="testData.service_type"
                :testId="testData.id"
                :testName="testData.title"
                :otherServiceLists="otherServiceLists"
                @closeModal="onClinicSliderClose"
                :openDrawer="openDrawer"
              ></ClinicSliderList>
            </div>
          </div>
          <div class="related-service md:hidden my-12" v-if="relatedServicesOptions.length > 1">
            <h2 class="flex text-2xl font-bold mb-4">Available As</h2>
            <div
              class="flex flex-col my-4 text-lg border-b border-light-gray"
              v-for="(relatedData, index) in relatedServicesOptions"
              :key="index"
            >
              <div class="text-lg py-1">
                <ProductRadioButton
                  :id="relatedData.id"
                  :title="relatedData?.service_type?.title"
                  :slug="relatedData.slug"
                  name="serviceType"
                  :extraText="$formatCurrency(relatedData.price)"
                  v-model="picked"
                  @change="updateRadioAndMove()"
                ></ProductRadioButton>
              </div>
            </div>
            <NuxtLink class="underline text-secondary cursor-pointer">
              <div class="flex justify-center mt-4">
                <NuxtImg
                  loading="lazy"
                  src="/icons/arrow-down.svg"
                  class="mx-1"
                  alt="down arrow icon"
                  width="16"
                  height="16"
                />
                <span @click="linkTitle('.how-it-works-wrapper')">Compare Our Services</span>
              </div>
            </NuxtLink>
          </div>
          <CommonLiveChatBlock></CommonLiveChatBlock>
        </div>
      </NuxtLayout>

      <div class="bg-secondary-lighter">
        <NuxtLayout name="center" class="pt-6 pb-8 how-it-works-wrapper">
          <CommonHowItWorks title-position="left" :services="services" :default-service="testData.service_type" />
        </NuxtLayout>
      </div>

      <NuxtLayout name="center">
        <ProductReview
          class="mt-20 md:mb-20"
          :reviewList="reviewResult.data"
          :totalReview="reviewResult.meta"
          totalReviewPosition="top"
        ></ProductReview>
      </NuxtLayout>

      <div
        class="bg-stone-white md:mt-24 border-t-[6px] border-primary md:my-8 py-8 product-info"
        v-if="testData.description"
      >
        <NuxtLayout name="center">
          <div class="flex justify-left mb-8 pt-4">
            <h2>{{ $t("testDetail.productInfo") }}</h2>
          </div>
          <div class="flex text-dark-blue md:space-x-12">
            <div class="hidden md:block w-full max-w-[240px] lg:max-w-[300px]">
              <div class="md:sticky self-start top-40">
                <div class="pb-2 product-info-toc">
                  <BaseTableOfContent
                    :content-ref="conditionContent"
                    header-tag="h3"
                    :transparent-background="true"
                    :show-header="false"
                  />
                </div>
                <div class="max-w-md mx-auto hidden md:block" v-if="pickedServiceType?.title != 'Home Test Kit'">
                  <BaseButton
                    :text="$t('testDetail.bookAppointment')"
                    class="w-full min-h-12 text-lg font-semibold !whitespace-break-spaces"
                    @click="openDrawerMap(testData)"
                  ></BaseButton>
                </div>
              </div>
            </div>
            <div class="w-full md:border-l md:border-turquoise-medium/30 md:px-8">
              <article>
                <div ref="conditionContent" class="condition-content product-content">
                  <div v-html="$stripHTML(testData.description)" class="mb-1"></div>
                </div>
              </article>
              <div class="max-w-md mt-8 mx-auto md:hidden" v-if="pickedServiceType?.title != 'Home Test Kit'">
                <BaseButton
                  :text="$t('testDetail.bookAppointment')"
                  class="w-full h-[60px] text-[22px] mb-2 md:mt-0 mt-8"
                  @click="openDrawerMap(testData)"
                ></BaseButton>
              </div>
            </div>
          </div>
        </NuxtLayout>
      </div>

      <CommonFaqAndLiveChatBlock :showQuestion="true" :faqData="faqData"></CommonFaqAndLiveChatBlock>

      <div class="bg-secondary-lighter" v-if="relatedTests.length > 0">
        <NuxtLayout name="center" class="pt-12 md:pt-20 pb-12">
          <CommonMostPopularTests
            :products="relatedTests"
            title="Related Tests"
            view-detail-button-title=""
            view-all-button-title=""
            :showViewAllButton="false"
          ></CommonMostPopularTests>
        </NuxtLayout>
      </div>
    </div>

    <template v-if="isModalOpen">
      <transition name="modalFade" appear>
        <div
          @click.self="closeVideoModal"
          class="fixed flex items-center z-50 first-letter: h-screen w-screen bottom-0 top-0 left-0 right-0 bg-black/25"
        >
          <div
            class="w-[90%] h-fit lg:w-[55%] ml-auto mr-auto align-top flex flex-col bg-white border rounded-lg aspect-video"
          >
            <video class="w-full rounded-md" controls :src="activeVideoUrl">
              Your browser does not support the video tag.
            </video>

            <div class="mt-4 mb-2 flex justify-center">
              <BaseButton class="w-[155px]" @click="closeVideoModal" :text="$t('common.btnClose')" variant="outlined" />
            </div>
          </div>
        </div>
      </transition>
    </template>

    <template v-if="isOutStockModalOpen">
      <ProductOutStockModal
        :isOutStockModalOpenProp="isOutStockModalOpen"
        @close="isOutStockModalOpen = false"
        :productID="testData.id"
      />
    </template>
  </div>
</template>

<script setup>
import _ from "lodash";
import { Carousel, Navigation as CarouselNavigation, Slide } from "vue3-carousel";
import "vue3-carousel/dist/carousel.css";
import { useCheckoutStore } from "~/stores/checkout";
import { useProductStore } from "~/stores/filter";
import { useGlobalStore } from "~/stores/global";

const globalStore = useGlobalStore();

function isSubscribeButtonEnabled(flag) {
  if (flag === true || flag === 1) return true;
  if (typeof flag === "string") {
    const normalized = flag.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  return false;
}

function isValidSubscriptionLink(link) {
  const trimmed = String(link ?? "").trim();
  if (!trimmed) return false;
  return /^https?:\/\//i.test(trimmed) || trimmed.startsWith("/");
}

const isModalOpen = ref(false);
const isOutStockModalOpen = ref(false);
const activeVideoUrl = ref("");
const stockStatus = ref("");
const stock = ref(null);
const productStore = useProductStore();
const { $formatCurrency, $stripHTML } = useNuxtApp();
const { t } = useI18n();
const testData = ref({});

const subscriptionLink = computed(() => String(testData.value?.subscription_link ?? "").trim());

const showSubscribeBlock = computed(() => {
  const inStock = Boolean(stock.value && stock.value !== "out");
  return (
    inStock &&
    isSubscribeButtonEnabled(testData.value?.display_subscribe_button) &&
    isValidSubscriptionLink(subscriptionLink.value)
  );
});

const relatedTests = ref([]);
const primaryImage = ref(null);
const route = useRoute();
const mapStore = useMapStore();
const picked = ref(route.params.slug);
const pickedServiceType = reactive({
  slug: "",
  title: "",
});
const collectionMethods = ref();
const relatedServicesOptions = ref([]);
const faqData = ref([]);
const lowestPrice = ref("");
const openDrawer = ref(false);
const showModal = ref(false);
const modalDetails = ref({});
const services = ref([]);
const infectionsRow = shallowRef(1);
const conditionContent = ref(null);
const otherServiceLists = ref();
const checkoutStore = useCheckoutStore();
const filterOptions = ref({});
const testHelper = useTestHelper();

filterOptions.value = await useFilterOptions();

const breakpoints = {
  0: {
    itemsToShow: 2,
    snapAlign: "start",
  },
  1024: {
    itemsToShow: 4,
    snapAlign: "start",
  },
};

await productStore.setFilterOptions(filterOptions.value);
const { data: contentData, refresh } = await useTestScreen(route.params.slug);
if (contentData.value) {
  testData.value = contentData.value.data;
  primaryImage.value = testData.value?.primary_image || null;
  stock.value = testData.value?.stock || null;
  if (stock.value) {
    if (stock.value == "in") {
      stockStatus.value = t("testDetail.inStock");
    } else if (stock.value == "low") {
      stockStatus.value = t("testDetail.lowStock");
    } else {
      stockStatus.value = t("testDetail.outOfStock");
    }
  }

  faqData.value = contentData.value.faqs;

  collectionMethods.value = testData.value.collection_methods.map((data) => " " + data.title).toString();
  var subset = _.pick(testData.value, ["id", "title", "summary", "price", "slug", "service_type"]);
  var subset2 = testData.value.related_services.map(({ id, title, summary, slug, price, service_type }) => ({
    id,
    title,
    summary,
    slug,
    price,
    service_type,
  }));
  const serviceList = [...[subset], ...subset2];
  relatedServicesOptions.value = serviceList.sort((a, b) => (a.slug < b.slug ? 1 : -1));

  const newObject = relatedServicesOptions.value.find((data) => data.slug == picked.value)?.service_type || {};

  Object.assign(pickedServiceType, newObject);
  //debugJson(pickedServiceType);

  otherServiceLists.value = relatedServicesOptions.value.filter(
    (data) => data.service_type.slug != "everywhere-nurse-service"
  );
  services.value = testData.value.related_services.map(function (related_service) {
    return related_service.service_type.slug;
  });
  services.value = [testData.value.service_type.slug].concat(services.value);

  //selectedServices.value = relatedServicesOptions.value.find((data) => data.slug == picked.value)
  lowestPrice.value = $formatCurrency(Math.min(...relatedServicesOptions.value.map((data) => data.price)));

  infectionsRow.value = Math.ceil(testData.value.infections.length / 2);
}

const { data: relatedTestsData } = await useRelatedTests(route.params.slug);
if (relatedTestsData.value) {
  relatedTests.value = relatedTestsData.value.data;
}

const titleBarBackground = computed(() => {
  return pickedServiceType.slug ? testHelper.getTitleBarBackground(pickedServiceType.slug) : "bg-space-cadet";
});

const serviceIcon = computed(() => {
  if (pickedServiceType?.slug) {
    return testHelper.getServiceIcon(pickedServiceType.slug);
  }
  return "/icons/services/clinic-test.svg";
});

const isLarge = useMinScreenWidth(1024);
const shouldShowNavigation = computed(() => {
  const itemCount = testData.value?.additional_media?.length || 0;
  return isLarge.value ? itemCount > 4 : itemCount > 2;
});

const setPrimaryImage = (image) => {
  primaryImage.value = image;
};

const openVideoModal = (video) => {
  activeVideoUrl.value = video.url;
  isModalOpen.value = true;
};

const closeVideoModal = () => {
  isModalOpen.value = false;
  activeVideoUrl.value = "";
};

const openOutStockModal = () => {
  isOutStockModalOpen.value = true;
};

watch(
  () => route.params.slug,
  () => {
    refresh();
  }
);
watch(
  () => testData.value?.service_type.slug,
  () => {
    refreshFaq();
  }
);

const { data: reviewResult } = await useBlockReview(1);

function updateRadioAndMove() {
  navigateTo(picked.value);
}

function formatDuration(duration) {
  if (!duration || String(duration).trim().toLowerCase() === "null") {
    const naLabel =
      t("testDetail.naLabel") === "testDetail.naLabel" ? t("testDetail.naLabel") : t("testDetail.naLabel");
    return naLabel;
  }

  let singularLabel = t("testDetail.dayLabel");
  let pluralLabel = t("testDetail.daysLabel");

  if (singularLabel === "testDetail.dayLabel") {
    singularLabel = t("testDetail.dayLabel");
  }
  if (pluralLabel === "testDetail.daysLabel") {
    pluralLabel = t("testDetail.daysLabel");
  }

  const durationStr = String(duration).trim();

  const dayRegex = /\bday(s)?\b/i;
  if (dayRegex.test(durationStr)) {
    return durationStr;
  }

  const numericValue = parseFloat(durationStr);
  if (!isNaN(numericValue)) {
    const label = numericValue === 1 ? singularLabel : pluralLabel;
    return `${numericValue} ${label}`;
  }

  return `${durationStr} ${pluralLabel}`;
}

const collectionMethodDescription = testData.value.collection_methods.map(function (collection_method) {
  return {
    title: collection_method.title,
    description: t("testDetail.collectionMethod.modalDescription." + collection_method.slug),
  };
});

const testDetail = [
  {
    title: t("testDetail.incubationPeriod.title"),
    data: formatDuration(testData.value.incubation_period),
    description: t("testDetail.incubationPeriod.modalDescription"),
    descriptionList: [],
    dataProp: "incubation_period",
  },
  {
    title: t("testDetail.collectionMethod.title"),
    data: `${collectionMethods.value}`,
    description: "",
    descriptionList: collectionMethodDescription,
    dataProp: "collection_methods",
  },
  {
    title: t("testDetail.turnaroundTime.title"),
    data: formatDuration(testData.value.turnaround_time),
    description: t("testDetail.turnaroundTime.modalDescription"),
    descriptionList: [],
    dataProp: "turnaround_time",
  },
];

const testAdditionalDetails = testDetail.filter((data) => testData.value[data.dataProp] != "");

const openDrawerMap = async (item) => {
  checkoutStore.addTest(item);
  productStore.setSelectedTest(item.id);
  openDrawer.value = true;
};

function onIconclick(data) {
  modalDetails.value = data;
  openModal();
}

const openModal = () => {
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
};
const onClinicSliderClose = () => {
  openDrawer.value = false;
  //on close of clinic slider list we don't want to clear the selected test
  //checkoutStore.removeTestLists()
};

function linkTitle(elementClass) {
  const elements = document.querySelectorAll(elementClass);
  let position;
  for (let i = 0; i < elements.length; i++) {
    position = elements[i].getBoundingClientRect();
    if (position.top > 0) {
      break;
    }
  }
  // scrolls to 20px above element
  window.scrollTo({
    behavior: "smooth",
    top: position.top + window.scrollY - 100,
  });
}

useMetaTitles(contentData.value.data, "test-detail");

const breadcrumbs = getBreadcrumbs("test-detail", contentData.value.data);

generateSchema("test-detail", faqData.value);

const kitProduct = computed(() => {
  if (!testData.value?.slug) return null;

  return {
    id: testData.value.id,
    test_title: testData.value.title,
    service_title: pickedServiceType.title,
    quantity: 1,
    price: parseFloat(testData.value.price),
    net_price: 0,
    discount: 0,
  };
});
</script>

<style scoped>
.section-topic a.active {
  font-weight: bold;
}

.infection-rows {
  grid-template-rows: repeat(var(--rows, 1), minmax(0, 1fr));
}
</style>
<style>
.product-info-toc > div > div {
  @apply mb-0;
}
</style>
